import { useEffect, useRef } from "react";

export default function ParticleBackground({ className = "opacity-40" }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width = 0, height = 0, dpr = 1;
    let rafId = 0;

    const themeLine = [86, 109, 135]; // #566D87
    const themeDot = [255, 212, 4];   // #FFD404

    const particles = [];

    const calcCount = () => {
      const area = width * height;
      const density = 0.00007; // tuned for performance
      const count = Math.max(30, Math.min(110, Math.floor(area * density)));
      return count;
    };

    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      const styleWidth = canvas.clientWidth || canvas.offsetWidth || window.innerWidth;
      const styleHeight = canvas.clientHeight || canvas.offsetHeight || window.innerHeight;
      width = styleWidth;
      height = styleHeight;
      canvas.width = Math.round(styleWidth * dpr);
      canvas.height = Math.round(styleHeight * dpr);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      const target = calcCount();
      if (particles.length < target) {
        for (let i = particles.length; i < target; i++) {
          particles.push({
            x: Math.random() * width,
            y: Math.random() * height,
            vx: (Math.random() - 0.5) * 0.35,
            vy: (Math.random() - 0.5) * 0.35,
            r: 1 + Math.random() * 1.75,
          });
        }
      } else if (particles.length > target) {
        particles.length = target;
      }
    };

    const maxDist = 120;

    const step = () => {
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < maxDist * maxDist) {
            const d = Math.sqrt(d2);
            const alpha = Math.max(0, 1 - d / maxDist) * 0.55;
            ctx.strokeStyle = `rgba(${themeLine[0]}, ${themeLine[1]}, ${themeLine[2]}, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
      }

      ctx.fillStyle = `rgba(${themeDot[0]}, ${themeDot[1]}, ${themeDot[2]}, 0.85)`;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x <= 0 || p.x >= width) p.vx *= -1;
        if (p.y <= 0 || p.y >= height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafId = requestAnimationFrame(step);
    };

    resize();
    window.addEventListener('resize', resize);
    rafId = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      aria-hidden="true"
    />
  );
}
